import { getTurnstileToken, type TurnstileMintControl } from '@client/utils/turnstile/turnstile-token';
import type { Operation } from '@trpc/client';

export type PublicFormCaptchaTokens = {
    turnstileToken: string | null;
};

export type PublicFormCaptchaTokenOptions = {
    /**
     * Priority handle for the Turnstile mint. Speculative operations pass a 'background'
     * control so their mint cannot starve foreground requests in the serial mint queue
     * (CUR-901). Omitted ⇒ foreground.
     */
    turnstileMintControl?: TurnstileMintControl;
};

/**
 * Mint the CAPTCHA credential for a protected public-form request.
 *
 * A failed or blocked mint resolves to `null` rather than throwing: the request is sent
 * without a credential and the server decides — orgs with `isRecaptchaEnabled = false`
 * (and the operator kill-switch) are honoured server-side, and everyone else gets the
 * same UNAUTHORIZED a client-side abort would imply — so throwing here would only defeat
 * the per-org opt-out.
 */
export async function getPublicFormCaptchaTokens(
    action: string,
    options?: PublicFormCaptchaTokenOptions,
): Promise<PublicFormCaptchaTokens> {
    const turnstileToken = await getTurnstileToken(action, {
        mintControl: options?.turnstileMintControl,
    }).catch(() => null);

    return { turnstileToken };
}

export function getPublicFormCaptchaTokensForOperation(
    op: Pick<Operation, 'context' | 'path'>,
): Promise<PublicFormCaptchaTokens> {
    const action = op.path.replace(/[^a-zA-Z]/g, '_');
    return getPublicFormCaptchaTokens(action, { turnstileMintControl: op.context.captchaMintControl });
}
